import { Button, Input, Label } from '@sea-snag/ui';
import { HTTPError } from 'ky';
import { AlertCircle, Key, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useValidateKey } from '../../hooks/use-setup';
import type { CompanyOption } from '../../lib/api-client';

interface SetupKeyStepProps {
  orgPid: string;
  onValidated: (companies: CompanyOption[], apiKey: string) => void;
  isConnecting?: boolean;
  connectError?: Error | null;
}

export function SetupKeyStep({
  orgPid,
  onValidated,
  isConnecting,
  connectError,
}: SetupKeyStepProps) {
  const [apiKey, setApiKey] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const validateKey = useValidateKey(orgPid);

  const extractErrorMessage = useCallback(async (error: unknown): Promise<string> => {
    if (error instanceof HTTPError) {
      try {
        const body = (await error.response.json()) as { error?: { message?: string } };
        if (body?.error?.message) return body.error.message;
      } catch {
        // ignore parse errors
      }
    }
    return 'Invalid API key. Please check and try again.';
  }, []);

  function handleValidate() {
    if (!apiKey.trim()) return;
    setErrorMessage(null);

    validateKey.mutate(
      { provider: 'mmk', apiKey: apiKey.trim() },
      {
        onSuccess: (data) => {
          onValidated(data.companies, apiKey.trim());
        },
        onError: (error) => {
          void extractErrorMessage(error).then(setErrorMessage);
        },
      }
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Key className="size-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">Connect your fleet</h3>
        <p className="text-sm text-muted-foreground">
          Enter your MMK Booking Manager API key to sync your yachts, availability and bookings.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="api-key">API Key</Label>
        <Input
          id="api-key"
          type="password"
          placeholder="Enter your MMK API key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleValidate();
          }}
          disabled={validateKey.isPending}
        />
        {(errorMessage || connectError) && (
          <div className="flex items-center gap-1.5 text-sm text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            <span>{connectError ? 'Failed to connect. Please try again.' : errorMessage}</span>
          </div>
        )}
      </div>

      <Button
        onClick={handleValidate}
        disabled={!apiKey.trim() || validateKey.isPending || isConnecting}
      >
        {isConnecting ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Connecting...
          </>
        ) : validateKey.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Validating...
          </>
        ) : (
          'Validate Key'
        )}
      </Button>
    </div>
  );
}
